import { degreeToRadian } from "common/utils/math";
import { planeNew } from "geom/plane";
import type { Plane } from "geom/planeTypes";
import { parseFormula } from "./formula/formulaParsing";

export function makeXYPlane(x: number = 0, y: number = 0, z: number = 0, angle: number = 0): Plane {
  const plane = planeNew();
  angle = degreeToRadian(angle);
  plane.originX = x;
  plane.originY = y;
  plane.originZ = z;

  plane.uX = Math.cos(angle);
  plane.uY = Math.sin(angle);
  plane.vX = -Math.sin(angle);
  plane.vY = Math.cos(angle);

  return plane;
}

export function f2i(feet: number, inches: number = 0): number {
  feet *= 12;
  if (inches > 0) {
    feet += inches;
  }
  return feet;
}

interface FeetInchesFraction {
  readonly sign: 1 | -1;
  readonly feet: number;
  readonly inches: number;
  readonly inchesFractionNumerator: number | null;
  readonly inchesFractionDenominator: number | null;
}

export function parseEvalFeetInchesFraction(input: string): number {
  const frac = parseFeetInchesFraction(input);
  if (frac === null) {
    return Number.NaN;
  }
  return evalFeetInchesFraction(frac);
}

// matches numbers like `3' 1/2"`
const feetInchesRegex = /\-?\s*([0-9]+\s*\')?\s*(-?\s*([0-9\.]+)?\s*(([0-9]+)\s*\/\s*([0-9]+))?\")?/;

// matches numbers like `3' 1/2`
const feetInchesRegexNoDoubleQuote = /\-?\s*([0-9]+\s*\')?\s*(-?\s*([0-9\.]+)?\s*(([0-9]+)\s*\/\s*([0-9]+))?)?/;

// identifies strings that are non-numeric
const nonNumericRegex = /[^0-9.Ee+-]/;

export function stringContainsOnlyNumericCharacters(input: string): boolean {
  return !nonNumericRegex.test(input);
}

export function parseFeetInchesFraction(input: string): FeetInchesFraction | null {
  const trimmedInput = input.trim();

  if (trimmedInput.length === 0) {
    return { sign: 1, feet: 0, inches: 0, inchesFractionDenominator: null, inchesFractionNumerator: null };
  }

  let res = feetInchesRegex.exec(trimmedInput);

  if (res === null || trimmedInput !== res[0]) {
    res = feetInchesRegexNoDoubleQuote.exec(trimmedInput);
  }

  // if the input doesn't look 12'-4 or 12'-4" or 12'4" or 12'4 then we can try and evaluate it
  // as a formula. These are ambiguous to the evaluator so we need to handle them separately.
  if (res === null || trimmedInput !== res[0]) {
    try {
      let preparedInput = trimmedInput;

      if (preparedInput.endsWith("'")) {
        preparedInput = preparedInput.slice(0, -1) + "*12";
      }

      preparedInput = preparedInput.replace(/'/g, "*12+");
      preparedInput = preparedInput.replace(/"/g, "");

      const expr = parseFormula(preparedInput);
      const res = expr.evaluate();

      if (typeof res === "number" && !isNaN(res)) {
        const feet = Math.floor(Math.abs(res / 12));
        const inches = Math.abs(res) % 12;

        return {
          sign: res < 0 ? -1 : 1,
          feet,
          inches,
          inchesFractionDenominator: null,
          inchesFractionNumerator: null,
        };
      }
    } catch (e) {
      console.log("invalid formula, ignoring...", e);
    }
  }

  if (res === null || trimmedInput !== res[0]) {
    // don't parse input that contains non-numeric characters
    // parseFloat otherwise consumes a number up to the first non-numeric character
    if (stringContainsOnlyNumericCharacters(input)) {
      const num = parseFloat(trimmedInput);
      if (!isNaN(num)) {
        return {
          sign: num < 0 ? -1 : 1,
          feet: 0,
          inches: Math.abs(num),
          inchesFractionDenominator: null,
          inchesFractionNumerator: null,
        };
      }
    }
    return null;
  }

  const obj: FeetInchesFraction = {
    sign: trimmedInput.startsWith("-") ? -1 : 1,
    feet: typeof res[1] === "string" ? parseFloat(res[1].replace("'", "").trim()) : 0,
    inches: typeof res[3] === "string" ? parseFloat(res[3].trim()) : 0,
    inchesFractionNumerator: typeof res[5] === "string" ? parseFloat(res[5].trim()) : null,
    inchesFractionDenominator: typeof res[6] === "string" ? parseFloat(res[6].trim()) : null,
  };

  if (
    isNaN(obj.feet) ||
    isNaN(obj.inches) ||
    (obj.inchesFractionNumerator !== null && isNaN(obj.inchesFractionNumerator)) ||
    (obj.inchesFractionDenominator !== null && isNaN(obj.inchesFractionDenominator))
  ) {
    return null;
  }

  return obj;
}

export function evalFeetInchesFraction({
  sign,
  feet,
  inches,
  inchesFractionNumerator,
  inchesFractionDenominator,
}: FeetInchesFraction) {
  return sign * (12 * feet + inches + (inchesFractionNumerator ?? 0) / (inchesFractionDenominator ?? 1));
}

export function formatInchesRounded(inches: number): string {
  inches = Math.round(inches);
  return formatInchesFraction(inches);
}

/** maxDenominator = 8 would yield to nearest 1/8 inch */

const INCH_FRACTION_MAX_DENOMINATOR = 8;
export const FORMAT_INCHES_FRACTION_MAX_DENOMINATOR_SHEET = 64;

export function formatInchesFraction(
  inches: number,
  maxDenominator = INCH_FRACTION_MAX_DENOMINATOR,
  allowZeroFeet: boolean = false,
  stackFractions: boolean = false,
): string {
  const sign = inches < 0 ? "-" : "";
  inches = Math.abs(inches);
  const feetComponent = Math.floor(inches / 12);
  const inchComponent = Math.floor(inches) % 12;
  const decimalInch = inches - Math.floor(inches);

  // early exit for round inches
  if (decimalInch === 0) {
    if (feetComponent === 0 && !allowZeroFeet) {
      return `${sign}${inchComponent}"`;
    }

    return `${sign}${feetComponent}'-${inchComponent}"`;
  }

  const decimalInchRounded = Math.round(decimalInch * maxDenominator);
  const fractionalResult = toFraction(decimalInchRounded, maxDenominator);
  const additionalInches = fractionalResult.isFraction ? 0 : fractionalResult.resultNum ?? 0;
  const additionalFeet = inchComponent + additionalInches === 12 ? 1 : 0;

  // if fraction rounding bubbles up to feet.
  if (additionalFeet === 1) {
    return `${sign}${feetComponent + additionalFeet}'-0"`;
  }

  // if fraction rounding bubbles up to inches.
  if (decimalInchRounded === 0 || !fractionalResult.isFraction) {
    if (feetComponent === 0 && !allowZeroFeet) {
      return `${sign}${inchComponent + additionalInches}"`;
    }
    return `${sign}${feetComponent}'-${inchComponent + additionalInches}"`;
  }

  let fractionalInches = fractionalResult.isFraction ? fractionalResult.resultString : "0";

  if (stackFractions) {
    if (fractionalInches === "1/8") {
      fractionalInches = "⅛";
    } else if (fractionalInches === "1/4") {
      fractionalInches = "¼";
    } else if (fractionalInches === "3/8") {
      fractionalInches = "⅜";
    } else if (fractionalInches === "1/2") {
      fractionalInches = "½";
    } else if (fractionalInches === "5/8") {
      fractionalInches = "⅝";
    } else if (fractionalInches === "3/4") {
      fractionalInches = "¾";
    } else if (fractionalInches === "7/8") {
      fractionalInches = "⅞";
    }
  }

  const inchesAndFractions = inchComponent === 0 ? `${fractionalInches}"` : `${inchComponent} ${fractionalInches}"`;
  if (feetComponent === 0 && !allowZeroFeet) {
    return `${sign}${inchesAndFractions}`;
  }
  return `${sign}${feetComponent}'-${inchesAndFractions}`;
}

interface FractionResult {
  isFraction: boolean;
  resultNum?: number;
  resultString?: string;
}

function toFraction(numerator: number, denominator: number): FractionResult {
  const greatestCommonD = (a: number, b: number): number => {
    return b ? greatestCommonD(b, a % b) : a;
  };
  const greatestCommonDenom = greatestCommonD(numerator, denominator);

  const numVal = numerator / greatestCommonDenom;
  const denVal = denominator / greatestCommonDenom;

  if (numVal === 0) {
    return { isFraction: false, resultNum: 0 };
  }
  if (numVal === denVal) {
    return { isFraction: false, resultNum: 1 };
  }

  return { isFraction: true, resultString: `${numVal}/${denVal}` };
}

export function isBelowInchFractionTolerance(inches: number, maxDenominator = INCH_FRACTION_MAX_DENOMINATOR) {
  return Math.abs(inches) < 1 / maxDenominator;
}
